import React from "react";

import Heading from "@common/components/Heading/Heading";
import { HelperText } from "@common/components/HelperText/HelperText";
import { useModal } from "@common/components/Modal/Modal";
import PageTitle from "@common/components/PageTitle/PageTitle";
import { Spinner } from "@common/components/Spinner/Spinner";
import { IntegrationType } from "@common/models/integrations";
import identifiers from "@common/util/identifiers.json";
import { t } from "@common/util/translator";
import { SentryCard } from "@main/pages/Administration/components/ExtensionsCards/SentryCard";
import DisconnectSentryModalBody from "@main/pages/Administration/components/ModalBody/DisconnectSentryModalBody";
import { useRedirectConsumer } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/consumers/shared/useRedirectConsumer";
import useExtensionsViewApiConsumer from "@main/pages/Administration/consumers/useExtensionsViewConsumer";

export default function ExtensionsView(): JSX.Element {
  const { disableSentry, selectedWorkspace, loading } = useExtensionsViewApiConsumer();
  const { startRedirection } = useRedirectConsumer({ workspace: selectedWorkspace, selectedAppId: "" }); // We don't need app id for sentry

  const {
    Modal: DisconnectSentryModal,
    setActive: setDisconnectSentryModalActive,
    modalProps,
  } = useModal({ size: "narrow" });

  if (!selectedWorkspace) {
    return <Spinner />;
  }

  const handleAddExtension = async () => {
    if (selectedWorkspace.sentry) {
      const success = await disableSentry();
      if (success) setDisconnectSentryModalActive(true);
    } else {
      startRedirection(IntegrationType.SENTRY);
    }
  };

  return (
    <>
      <PageTitle>
        <Heading
          as="h1"
          heading1
          marginBottom={8}
          testId={identifiers["workspaceAdministration.header.extensions"]}
        >
          {t("administration.extensions.title")}
        </Heading>
        <p>{t("administration.extensions.subtitle")}</p>
      </PageTitle>
      <HelperText marginBottom={32}>
        {"To forward Shake data to other tools, open Apps on the left › app › Feedback tab › Integrations."}
      </HelperText>
      <SentryCard
        isSentryActive={!!selectedWorkspace.sentry}
        handleActiveChange={handleAddExtension}
        disabled={loading}
      />
      <DisconnectSentryModalBody
        Modal={DisconnectSentryModal}
        internalProps={modalProps}
      />
    </>
  );
}
